// Tabs/EditSessionDrawer.tsx
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Drawer, Stack, Textarea, Group, Button, Select, Loader,
} from '@mantine/core';
import { IconPencil } from '@tabler/icons-react';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useRegistrationService,
  useSessionStatusService,
  useSessionProgressStatusService,
  RegistrationSessionModelView,
  UpdateRegistrationSessionRequest,
  SessionStatus,
  SessionProgressStatus,
} from '@actschurch/ccms-sdk';

interface EditSessionDrawerProps {
  opened: boolean;
  onClose: () => void;
  session: RegistrationSessionModelView | null;
  onSaved: () => void;
}

/**
 * Local form state. `statusID` / `progressStatusID` are part of
 * `UpdateRegistrationSessionRequest`, so the single `updateSession` call carries them; there is no
 * need for the separate `changeSessionStatus` / `changeSessionProgress` endpoints here.
 */
interface EditSessionFormState {
  details: string;
  notes: string;
  statusID: number;
  progressStatusID: number;
}

const EMPTY_FORM: EditSessionFormState = {
  details: '',
  notes: '',
  statusID: 0,
  progressStatusID: 0,
};

export default function EditSessionDrawer({ opened, onClose, session, onSaved }: EditSessionDrawerProps) {
  const registrationService = useRegistrationService();
  const sessionStatusService = useSessionStatusService();
  const sessionProgressStatusService = useSessionProgressStatusService();

  const [form, setForm] = useState<EditSessionFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // ── Status lookups, fetched once per open cycle ───────────────────────────
  const [statuses, setStatuses] = useState<SessionStatus[]>([]);
  const [progressStatuses, setProgressStatuses] = useState<SessionProgressStatus[]>([]);
  const [lookupsLoading, setLookupsLoading] = useState(false);
  const lookupsLoadedRef = useRef(false);

  useEffect(() => {
    if (!opened) {
      // Reset so re-opening the drawer later fetches fresh data again.
      lookupsLoadedRef.current = false;
      return;
    }
    if (lookupsLoadedRef.current) return;

    let cancelled = false;
    setLookupsLoading(true);
    Promise.all([sessionStatusService.getAll(), sessionProgressStatusService.getAll()])
      .then(([statusRes, progressRes]) => {
        if (cancelled) return;
        setStatuses(statusRes ?? []);
        setProgressStatuses(progressRes ?? []);
        lookupsLoadedRef.current = true;
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('Error loading session status lookups:', e);
        toast.error('Error', 'Could not load status options.');
      })
      .finally(() => {
        if (!cancelled) setLookupsLoading(false);
      });

    return () => { cancelled = true; };
  }, [opened, sessionStatusService, sessionProgressStatusService]);

  useEffect(() => {
    if (!session) return;
    setForm({
      details: session.details ?? '',
      notes: session.notes ?? '',
      statusID: session.statusID ?? 0,
      progressStatusID: session.progressStatusID ?? 0,
    });
  }, [session]);

  const statusOptions = useMemo(
    () => statuses
      .filter((s) => s.isActive)
      .map((s) => ({ value: String(s.statusID), label: s.name ?? `Status ${s.statusID}` })),
    [statuses],
  );

  const progressStatusOptions = useMemo(
    () => progressStatuses
      .filter((s) => s.isActive)
      .map((s) => ({ value: String(s.statusID), label: s.name ?? `Status ${s.statusID}` })),
    [progressStatuses],
  );

  const handleSave = async () => {
    if (!session) return;
    setSaving(true);
    try {
      const payload: UpdateRegistrationSessionRequest = {
        details: form.details ?? '',
        notes: form.notes ?? '',
        // Only send a status when one is chosen, so an unloaded lookup cannot blank it.
        ...(form.statusID ? { statusID: form.statusID } : {}),
        ...(form.progressStatusID ? { progressStatusID: form.progressStatusID } : {}),
      };
      await registrationService.updateSession(session.registrationSessionID, payload);
      toast.success('Session Updated', `"${session.name}" has been updated.`);
      onSaved();
      onClose();
    } catch (e) {
      console.error('Error updating session:', e);
      toast.error('Error', 'Could not update session. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconPencil size={18} />
          <span>Edit Session</span>
        </Group>
      }
      position="right"
      size="md"
      padding="md"
    >
      <Stack gap="md">
        <Select
          label="Status"
          placeholder={lookupsLoading ? 'Loading…' : 'Select a status'}
          data={statusOptions}
          value={form.statusID ? String(form.statusID) : null}
          onChange={(value) => setForm({ ...form, statusID: value ? Number(value) : 0 })}
          disabled={lookupsLoading}
          rightSection={lookupsLoading ? <Loader size={14} /> : undefined}
          searchable
          clearable
        />

        <Select
          label="Progress Status"
          placeholder={lookupsLoading ? 'Loading…' : 'Select a progress status'}
          data={progressStatusOptions}
          value={form.progressStatusID ? String(form.progressStatusID) : null}
          onChange={(value) => setForm({ ...form, progressStatusID: value ? Number(value) : 0 })}
          disabled={lookupsLoading}
          rightSection={lookupsLoading ? <Loader size={14} /> : undefined}
          searchable
          clearable
        />

        <Textarea
          label="Details"
          value={form.details ?? ''}
          onChange={(e) => setForm({ ...form, details: e.currentTarget.value })}
          autosize
          minRows={2}
        />

        <Textarea
          label="Facilitator / Interview Notes"
          value={form.notes ?? ''}
          onChange={(e) => setForm({ ...form, notes: e.currentTarget.value })}
          autosize
          minRows={2}
        />

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} loading={saving}>Save</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
